import styled from '@emotion/styled'
import React, { type FunctionComponent, useEffect, useState } from 'react'
import { fontSize, Label, SimpleSelect, spacing, Text } from '@retailer-platform/shared-components'
import { InputBase } from '@instacart/ids-core'
import { type Styles } from 'react-select'
import { useDomainMessages } from 'domains/products/utils/domain/intl'
import { type AttributeType } from 'domains/products/components/view/types/attributeOverride.types'

interface ValueFieldProps {
  attributeType: AttributeType
  selectedAttribute: string
  currentValue
  disabled: boolean
  onChange: (value) => void
}

const ContainerCentered = styled.div({
  flex: 1,
  display: 'flex',
  justifyContent: 'flex-start',
  gap: spacing.X12,
})

const InputLabelText = styled(Label)({
  width: '150px',
})

const controlStyles: Styles = {
  control: () => ({
    height: 46,
    fontSize: fontSize.X15,
    '[class*="IndicatorsContainer"]': {
      margin: 'auto',
    },
  }),
}

export const testIds = {
  valueInputBase: 'attribute-override-value-inputBase',
  valueTextArea: 'attribute-override-value-textArea',
  valueDropdown: 'attribute-override-value-dropdown',
  valuePlaceholder: 'attribute-override-value-placeholder',
  expiryDate: 'attribute-override-expiryDate',
}

export const ValueField: FunctionComponent<ValueFieldProps> = ({
  attributeType,
  selectedAttribute,
  currentValue,
  disabled,
  onChange,
}) => {
  const messages = useDomainMessages({
    valueLabel: 'productsDomain.attributeOverride.value.label',
    valuePlaceholder: 'productsDomain.attributeOverride.value.placeholder',
    valueOptionTrue: 'productsDomain.attributeOverride.value.dropdown.option.true',
    valueOptionFalse: 'productsDomain.attributeOverride.value.dropdown.option.false',
  })

  const valueOptions = [
    { label: messages.valueOptionTrue, value: true },
    { label: messages.valueOptionFalse, value: false },
  ]

  const [selectedValue, setSelectedValue] = useState<boolean>(currentValue)
  const [inputValue, setInputValue] = useState<string>(currentValue)

  useEffect(() => {
    setInputValue(currentValue)
    setSelectedValue(currentValue)
  }, [selectedAttribute, currentValue])

  const onSelectChange = (value: boolean) => {
    setSelectedValue(value)
    onChange(value)
  }

  const onInputChange = (value: string) => {
    setInputValue(value)
    onChange(value)
  }

  if (attributeType === 'boolean') {
    return (
      <SimpleSelect
        aria-label={testIds.valueDropdown}
        options={valueOptions}
        isModernTheme={true}
        isDisabled={disabled}
        onChange={onSelectChange}
        defaultValue={currentValue}
        value={selectedValue}
        styles={controlStyles}
      />
    )
  }

  if (attributeType === 'text') {
    return (
      <InputBase
        id={testIds.valueInputBase}
        data-testid={testIds.valueInputBase}
        value={inputValue}
        disabled={disabled}
        onChange={e => onInputChange(e.target.value)}
        css={{
          height: 46,
          fontSize: fontSize.X15,
        }}
      />
    )
  }

  return (
    <div
      data-testid={testIds.valuePlaceholder}
      css={{
        fontSize: fontSize.X12,
      }}
    >
      {messages.valuePlaceholder}
    </div>
  )
}
